<script setup lang="ts">
// ゆがみ: 専用モーダル。レイヤーの内容 (必要ならプロキシ縮小) をキャンバスに出し、
// ブラシで変位場を編集して確定時に全解像度で書き戻す。
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useModal } from '@/composables/useModalStack'
import { useI18n, type I18nKey } from '@/i18n'
import { pixelStore } from '@/pixels/pixelStore'
import { maskToAlpha } from '@/brush/fill'
import { boxDownsample } from '@/pixels/transform'
import { createDisplacement, applyBrush, renderRegion, resetDisplacement, type Displacement, type LiquifyTool } from '@/filters/liquify'
import SliderField from '@/components/common/SliderField.vue'

const emit = defineEmits<{ close: [] }>()
useModal('liquify')
const { t } = useI18n()
const docStore = useDocumentStore()

const layer = docStore.activeLayer?.kind === 'raster' ? docStore.activeLayer : null
const surface = layer ? pixelStore.get(layer.pixelId) : undefined
const bounds = surface?.contentBounds() ?? null
const MAX_PROXY_PIXELS = 3_000_000
const proxyScale = bounds ? Math.min(1, Math.sqrt(MAX_PROXY_PIXELS / (bounds.w * bounds.h))) : 1
// 全解像度のソース
const fullSrc = bounds && surface ? surface.readRect(bounds) : null
// プロキシ (編集解像度)
const pw = bounds ? Math.max(1, Math.round(bounds.w * proxyScale)) : 1
const ph = bounds ? Math.max(1, Math.round(bounds.h * proxyScale)) : 1
const src: Uint8ClampedArray | null = (() => {
  if (!fullSrc || !bounds) return null
  if (proxyScale === 1) return fullSrc
  const fx = bounds.w / pw, fy = bounds.h / ph
  return boxDownsample({ width: bounds.w, height: bounds.h, data: fullSrc }, fx, fy).data
})()
const field: Displacement = createDisplacement(pw, ph)
const out = src ? new Uint8ClampedArray(src) : null
// 凍結 (選択範囲外)
const freeze: Uint8ClampedArray | null = (() => {
  if (!bounds || !layer) return null
  const mask = docStore.selectionMask()
  if (!mask) return null
  const full = maskToAlpha(mask, { x: bounds.x + layer.offset.x, y: bounds.y + layer.offset.y, w: bounds.w, h: bounds.h })
  if (proxyScale === 1) return full
  const f = new Uint8ClampedArray(pw * ph)
  for (let y = 0; y < ph; y++) for (let x = 0; x < pw; x++) f[y * pw + x] = full[Math.min(bounds.h - 1, Math.floor(y / proxyScale)) * bounds.w + Math.min(bounds.w - 1, Math.floor(x / proxyScale))]!
  return f
})()

const tool = ref<LiquifyTool>('warp')
const size = ref(Math.max(10, Math.round(Math.min(pw, ph) / 6)))
const pressure = ref(50)
const showMesh = ref(false)
const tools: { id: LiquifyTool; label: I18nKey; glyph: string }[] = [
  { id: 'warp', label: 'liquify.warp', glyph: '↝' },
  { id: 'reconstruct', label: 'liquify.reconstruct', glyph: '↺' },
  { id: 'twirlCw', label: 'liquify.twirlCw', glyph: '↻' },
  { id: 'twirlCcw', label: 'liquify.twirlCcw', glyph: '↶' },
  { id: 'pucker', label: 'liquify.pucker', glyph: '⊙' },
  { id: 'bloat', label: 'liquify.bloat', glyph: '◎' }
]
const canvas = ref<HTMLCanvasElement | null>(null)
const mesh = ref<HTMLCanvasElement | null>(null)
// 表示倍率: ダイアログに収める
const viewScale = computed(() => {
  const maxW = Math.min(window.innerWidth * 0.7, 1200), maxH = Math.min(window.innerHeight * 0.62, 900)
  return Math.min(1, maxW / pw, maxH / ph)
})
const cssW = computed(() => Math.round(pw * viewScale.value))
const cssH = computed(() => Math.round(ph * viewScale.value))
let imageData: ImageData | null = null
let ctx: CanvasRenderingContext2D | null = null

function blit(region: { x: number; y: number; w: number; h: number } | null) {
  if (!ctx || !imageData || !out) return
  if (region) ctx.putImageData(imageData, 0, 0, region.x, region.y, region.w, region.h)
  else ctx.putImageData(imageData, 0, 0)
  if (showMesh.value) drawMesh()
}
function drawMesh() {
  const m = mesh.value
  if (!m) return
  const c = m.getContext('2d')!
  c.clearRect(0, 0, m.width, m.height)
  if (!showMesh.value) return
  const step = Math.max(8, Math.round(Math.min(pw, ph) / 24))
  c.strokeStyle = 'rgba(25,195,245,0.7)'
  c.lineWidth = 1
  const s = viewScale.value
  // 出力 p はソース p+d から来る → ソース格子点 q は出力 q-d(q) に見える (近似)
  const at = (x: number, y: number) => {
    const i = Math.min(ph - 1, y) * pw + Math.min(pw - 1, x)
    return { x: (x - field.dx[i]!) * s, y: (y - field.dy[i]!) * s }
  }
  c.beginPath()
  for (let y = 0; y <= ph; y += step) {
    for (let x = 0; x <= pw; x += step) {
      const p = at(x, y)
      if (x === 0) c.moveTo(p.x, p.y)
      else c.lineTo(p.x, p.y)
    }
  }
  for (let x = 0; x <= pw; x += step) {
    for (let y = 0; y <= ph; y += step) {
      const p = at(x, y)
      if (y === 0) c.moveTo(p.x, p.y)
      else c.lineTo(p.x, p.y)
    }
  }
  c.stroke()
}

onMounted(() => {
  const c = canvas.value
  if (!c || !src || !out) return
  c.width = pw
  c.height = ph
  ctx = c.getContext('2d')!
  imageData = new ImageData(new Uint8ClampedArray(out.buffer, out.byteOffset, out.byteLength) as unknown as Uint8ClampedArray<ArrayBuffer>, pw, ph)
  if (mesh.value) {
    mesh.value.width = cssW.value
    mesh.value.height = cssH.value
  }
  blit(null)
})

let last: { x: number; y: number } | null = null
let dragging = false
function toProxy(e: PointerEvent) {
  const r = canvas.value!.getBoundingClientRect()
  return { x: ((e.clientX - r.left) / r.width) * pw, y: ((e.clientY - r.top) / r.height) * ph }
}
function stroke(p: { x: number; y: number }, prev: { x: number; y: number } | null) {
  if (!src || !out) return
  const region = applyBrush(field, { tool: tool.value, x: p.x, y: p.y, radius: size.value / 2, pressure: pressure.value / 100, mx: prev ? p.x - prev.x : 0, my: prev ? p.y - prev.y : 0, freeze })
  if (!region) return
  renderRegion(src, pw, ph, field, region, out)
  blit(region)
}
function onDown(e: PointerEvent) {
  if (e.button !== 0) return
  ;(e.currentTarget as Element).setPointerCapture(e.pointerId)
  dragging = true
  const p = toProxy(e)
  last = p
  if (tool.value !== 'warp') stroke(p, null)
}
function onMove(e: PointerEvent) {
  if (!dragging) return
  const p = toProxy(e)
  if (tool.value === 'warp') {
    // 大きな移動は分割して滑らかに
    const prev = last ?? p
    const dist = Math.hypot(p.x - prev.x, p.y - prev.y)
    const steps = Math.max(1, Math.ceil(dist / Math.max(2, size.value / 8)))
    let q = prev
    for (let i = 1; i <= steps; i++) {
      const n = { x: prev.x + ((p.x - prev.x) * i) / steps, y: prev.y + ((p.y - prev.y) * i) / steps }
      stroke(n, q)
      q = n
    }
  } else stroke(p, null)
  last = p
}
function onUp() {
  dragging = false
  last = null
}
function restoreAll() {
  if (!src || !out) return
  resetDisplacement(field)
  out.set(src)
  blit(null)
}
function ok() {
  if (!layer || !bounds || !fullSrc) {
    emit('close')
    return
  }
  const result = new Uint8ClampedArray(fullSrc.length)
  renderRegion(fullSrc, bounds.w, bounds.h, field, { x: 0, y: 0, w: bounds.w, h: bounds.h }, result, proxyScale)
  docStore.writeLayerRect(layer.id, bounds, result, 'history.liquify')
  emit('close')
}
onBeforeUnmount(() => {
  imageData = null
  ctx = null
})
</script>

<template>
  <div class="modal-backdrop" @pointerdown.self="emit('close')">
    <div class="modal liquify" @keydown.esc="emit('close')">
      <h2>{{ t('filter.liquify').replace(/\.\.\.$/, '') }}</h2>
      <p v-if="!src" class="muted">{{ t('liquify.empty') }}</p>
      <div v-else class="body">
        <div class="tools">
          <button v-for="tl in tools" :key="tl.id" class="tool" :class="{ active: tool === tl.id }" :title="t(tl.label)" @click="tool = tl.id">{{ tl.glyph }}</button>
        </div>
        <div class="stage" :style="{ width: cssW + 'px', height: cssH + 'px' }">
          <canvas ref="canvas" class="img" :style="{ width: cssW + 'px', height: cssH + 'px' }" @pointerdown="onDown" @pointermove="onMove" @pointerup="onUp" @pointercancel="onUp" />
          <canvas ref="mesh" class="mesh" :style="{ width: cssW + 'px', height: cssH + 'px' }" />
        </div>
        <div class="side">
          <SliderField :label="t('liquify.size')" :value="size" :min="4" :max="Math.max(20, Math.min(pw, ph))" :step="1" unit="px" @input="v => (size = v)" @change="v => (size = v)" />
          <SliderField :label="t('liquify.pressure')" :value="pressure" :min="1" :max="100" :step="1" unit="%" @input="v => (pressure = v)" @change="v => (pressure = v)" />
          <label class="row"><input v-model="showMesh" type="checkbox" @change="drawMesh" /><span>{{ t('liquify.mesh') }}</span></label>
          <button @click="restoreAll">{{ t('liquify.restoreAll') }}</button>
          <p v-if="proxyScale < 1" class="muted small">{{ t('liquify.proxy', { pct: Math.round(proxyScale * 100) }) }}</p>
          <p v-if="freeze" class="muted small">{{ t('liquify.frozen') }}</p>
        </div>
      </div>
      <div class="actions">
        <button @click="emit('close')">{{ t('dialog.cancel') }}</button>
        <button class="primary" :disabled="!src" @click="ok">{{ t('dialog.ok') }}</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.liquify {
  max-width: 96vw;
}
.body {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}
.tools {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.tool {
  width: 30px;
  height: 30px;
  padding: 0;
  font-size: 16px;
}
.tool.active {
  background: var(--accent);
  color: #fff;
}
.stage {
  position: relative;
  background: repeating-conic-gradient(var(--checker-a) 0 25%, var(--checker-b) 0 50%) 0 0 / 16px 16px;
  border: 1px solid var(--line-strong);
}
.img,
.mesh {
  position: absolute;
  left: 0;
  top: 0;
  display: block;
}
.img {
  cursor: crosshair;
  touch-action: none;
}
.mesh {
  pointer-events: none;
}
.side {
  width: 220px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.small {
  font-size: 10px;
  margin: 0;
}
</style>
